import { FC, useEffect, useState, useMemo } from "react";
import { Form, Select, InputNumber, Input, Button, Switch, Checkbox, TimePicker, Tooltip } from "antd";
import { ServiceDatePicker } from "../booking-form/service-tickets-form/service-date-picker";
import { Supplies } from "../booking-form/service-tickets-form/supplies";
import dayjs from "dayjs";
import { MapPin, X, Plus, Trash2, Package, Wrench, User, Clock, Car, Calculator } from "lucide-react";
import { useDetailBookingStore } from "./detail-booking.store";
import { useCreateServiceTicket, useUpdateServiceTicket } from "~/services/service-ticket";
import { ServiceType, JobStatus, PackingTypes, Urgency, VesselStatus, Maintenance } from "~/utils/constants";
import { useNotification } from "~/hooks";
import { useGetClient } from "~/services/client";
import { useGetActiveInventoryTemplates } from "~/services/inventory-templates/inventory-templates.queries";
import { useGetVesselsForDropdown } from "~/services/vessel/vessel.queries";
import { formatFullAddress } from "~/utils";
import { useQueryClient } from "@tanstack/react-query";
import { BOOKING_KEYS } from "~/services/booking";
import { addOnServicesApi } from "~/services/add-on-services/add-on-services.api";
import type { AddOnServiceDto } from "~/services/add-on-services/add-on-services.types";
import { serviceIcons } from "~/utils/service-icons";
import { TServiceTicket, TServiceTicketSupplyItem } from "~/types";
import { useGetPackingMetrics, serviceMetricsApi } from "~/services/service-metrics";
import { StaffAssignment } from "../booking-form/service-tickets-form/staff-assignment";
import { NetworkConnect } from "../booking-form/service-tickets-form/network-connect";

const { TextArea } = Input;

// Map ServiceType to backend add-on service type
const serviceTypeToAddOnType: Record<ServiceType, "WAREHOUSE" | "FABRICATION" | "GENERAL"> = {
  [ServiceType.COLLECTION]: "GENERAL",
  [ServiceType.DELIVERY]: "GENERAL",
  [ServiceType.ON_SITE_SERVICE]: "GENERAL",
  [ServiceType.FABRICATION_SERVICE]: "FABRICATION",
  [ServiceType.WAREHOUSE_SERVICE]: "WAREHOUSE",
};

interface SelectedTemplate {
  templateId: string;
  templateName: string;
  quantity: number;
  packingType: PackingTypes | null;
  wePack: boolean; // true = we are packing, false = already packed
}

export const EstimateServiceForm: FC = () => {
  const {
    booking,
    showEstimateForm,
    setShowEstimateForm,
    editingEstimateService,
    setEditingEstimateService
  } = useDetailBookingStore();
  const [form] = Form.useForm();
  const { success, error } = useNotification();
  const [useQuickLocation, setUseQuickLocation] = useState(true);
  const [selectedTemplates, setSelectedTemplates] = useState<SelectedTemplate[]>([]);
  const [addOnServices, setAddOnServices] = useState<AddOnServiceDto[]>([]);
  const [selectedAddOnServices, setSelectedAddOnServices] = useState<string[]>([]);
  const [selectedServiceType, setSelectedServiceType] = useState<ServiceType>(ServiceType.COLLECTION);
  const [calculatedSupplies, setCalculatedSupplies] = useState<{supplyId: string; supplyName: string; quantity: number; unitCost?: number}[]>([]);
  const [isCalculatingSupplies, setIsCalculatingSupplies] = useState(false);
  const [supplyItems, setSupplyItems] = useState<Omit<TServiceTicketSupplyItem, "id">[]>([]);
  const [selectedStaffIds, setSelectedStaffIds] = useState<string[]>([]);
  const [selectedNetworkRouteId, setSelectedNetworkRouteId] = useState<string | undefined>();
  const [selectedVendorId, setSelectedVendorId] = useState<string | undefined>();
  const [selectedShuttleId, setSelectedShuttleId] = useState<string | undefined>();
  const qc = useQueryClient();

  // Watch the service date for staff availability
  const serviceDate = Form.useWatch("serviceDate", form);

  // Check if editing (has real ID) vs duplicating (has data but empty/no ID)
  const isEditing = !!editingEstimateService?.id;

  // Fetch client to get shipping addresses
  const { data: client } = useGetClient(booking?.clientId);

  // Fetch active inventory templates
  const { data: inventoryTemplates } = useGetActiveInventoryTemplates();

  // Fetch vessels
  const { data: vessels } = useGetVesselsForDropdown();

  // Fetch packing metrics for material cost calculation
  const { data: packingMetrics } = useGetPackingMetrics();

  // Calculate material cost based on template dimensions and packing type
  // Uses materialRatePerSqFt (materials only, no labor)
  const calculateMaterialCost = (
    length: number,
    width: number,
    height: number,
    packingType: PackingTypes | null,
    quantity: number
  ): number => {
    if (!packingType || !packingMetrics || !length || !width || !height) {
      return 0;
    }

    const metric = packingMetrics.find((m) => m.packingType === packingType);
    if (!metric) return 0;

    // Use materialRatePerSqFt if available, otherwise fall back to baseRate
    const materialRate = Number((metric as any).materialRatePerSqFt) || 0;
    const baseRate = Number(metric.baseRate) || 0;
    const ratePerSqFt = materialRate || baseRate;

    // Calculate surface area with 5% buffer
    const bufferedL = length * 1.05;
    const bufferedW = width * 1.05;
    const bufferedH = height * 1.05;
    const surfaceAreaSqFt =
      (2 * (bufferedL * bufferedW + bufferedL * bufferedH + bufferedW * bufferedH)) / 144;

    // Material cost per item
    const materialCostPerItem = surfaceAreaSqFt * ratePerSqFt;
    return materialCostPerItem * quantity;
  };

  const createMutation = useCreateServiceTicket();
  const updateMutation = useUpdateServiceTicket();

  // Fetch add-on services based on selected service type
  useEffect(() => {
    const fetchAddOnServices = async () => {
      try {
        const addOnType = serviceTypeToAddOnType[selectedServiceType];
        const services = await addOnServicesApi.getAll({ type: addOnType });
        setAddOnServices(services.filter(s => s.isActive));
      } catch (err) {
        console.error("Failed to fetch add-on services:", err);
      }
    };
    fetchAddOnServices();
  }, [selectedServiceType]);

  // Reset form when opening
  useEffect(() => {
    if (showEstimateForm) {
      if (editingEstimateService) {
        // Editing mode - populate form with existing data
        const service = editingEstimateService;
        form.setFieldsValue({
          serviceType: service.serviceType,
          serviceDate: service.serviceDatetime ? dayjs(service.serviceDatetime) : undefined,
          startTime: service.startDatetime ? dayjs(service.startDatetime) : undefined,
          urgency: service.urgency || Urgency.NORMAL,
          vesselId: service.vessel?.id,
          contactName: service.tempLocationData?.name,
          quickCity: service.tempLocationData?.city,
          quickState: service.tempLocationData?.state,
          quickZip: service.tempLocationData?.zip,
          locationId: service.location?.id,
          notes: service.scope || service.staffNotes,
          duration: service.duration ? Number(service.duration) : undefined,
          travelTime: service.travelTime ? Number(service.travelTime) : 0.5,
        });
        setSelectedServiceType(service.serviceType || ServiceType.COLLECTION);
        setUseQuickLocation(!!service.tempLocationData);

        // Populate templates if they exist
        if (service.estimateTemplates && service.estimateTemplates.length > 0) {
          setSelectedTemplates(service.estimateTemplates.map((t: any) => ({
            templateId: t.templateId || t.id,
            templateName: t.templateName || t.name,
            quantity: t.quantity || 1,
            packingType: t.packingType || null,
            wePack: t.wePack ?? true, // Default to true if not set
          })));
        } else {
          setSelectedTemplates([]);
        }

        // Populate add-on services
        if (service.addOnServices && service.addOnServices.length > 0) {
          setSelectedAddOnServices(service.addOnServices.map((s: any) => s.id));
        } else {
          setSelectedAddOnServices([]);
        }

        // Populate supply items
        if (service.supplyItems && service.supplyItems.length > 0) {
          setSupplyItems(service.supplyItems);
        } else {
          setSupplyItems([]);
        }
        setCalculatedSupplies([]);

        // Populate staff
        if (service.staffs && service.staffs.length > 0) {
          setSelectedStaffIds(service.staffs.map((s: any) => s.id));
        } else {
          setSelectedStaffIds([]);
        }

        // Populate network connect
        setSelectedNetworkRouteId(service.networkRouteId || undefined);
        setSelectedVendorId(service.vendorId || undefined);
        setSelectedShuttleId(service.shuttleId || undefined);
      } else {
        // Add mode - check for quick estimate data from sessionStorage
        const quickEstimateKey = `quickEstimate_${booking?.id}`;
        const quickEstimateDataStr = sessionStorage.getItem(quickEstimateKey);
        
        if (quickEstimateDataStr) {
          try {
            const quickEstimateData = JSON.parse(quickEstimateDataStr);
            
            // Pre-fill form with quick estimate data
            form.setFieldsValue({
              urgency: Urgency.NORMAL,
              serviceType: quickEstimateData.serviceType || ServiceType.COLLECTION,
              travelTime: quickEstimateData.travelTime || 0.5,
              duration: quickEstimateData.duration || 1,
              vesselId: quickEstimateData.vesselId || undefined,
              notes: quickEstimateData.notes || (quickEstimateData.additionalInfo ? `Quick Estimate Info: ${quickEstimateData.additionalInfo}` : ''),
            });
            
            setSelectedServiceType(quickEstimateData.serviceType || ServiceType.COLLECTION);
            setUseQuickLocation(true);
            
            // Pre-fill templates
            if (quickEstimateData.templates && quickEstimateData.templates.length > 0) {
              setSelectedTemplates(quickEstimateData.templates);
            } else {
              setSelectedTemplates([]);
            }
            
            setSelectedAddOnServices([]);
            setSupplyItems([]);
            setCalculatedSupplies([]);
            setSelectedStaffIds([]);
            
            // Clear the sessionStorage after using it
            sessionStorage.removeItem(quickEstimateKey);
          } catch (err) {
            console.error('Failed to parse quick estimate data:', err);
            // Fall back to defaults
            form.resetFields();
            form.setFieldValue('urgency', Urgency.NORMAL);
            form.setFieldValue('serviceType', ServiceType.COLLECTION);
            form.setFieldValue('travelTime', 0.5);
            setSelectedServiceType(ServiceType.COLLECTION);
            setUseQuickLocation(true);
            setSelectedTemplates([]);
            setSelectedAddOnServices([]);
            setSupplyItems([]);
            setCalculatedSupplies([]);
            setSelectedStaffIds([]);
            setSelectedNetworkRouteId(undefined);
            setSelectedVendorId(undefined);
            setSelectedShuttleId(undefined);
          }
        } else {
          // No quick estimate data - reset everything
          form.resetFields();
          form.setFieldValue('urgency', Urgency.NORMAL);
          form.setFieldValue('serviceType', ServiceType.COLLECTION);
          form.setFieldValue('travelTime', 0.5); // Default 30 min travel time
          setSelectedServiceType(ServiceType.COLLECTION);
          setUseQuickLocation(true);
          setSelectedTemplates([]);
          setSelectedAddOnServices([]);
          setSupplyItems([]);
          setCalculatedSupplies([]);
          setSelectedStaffIds([]);
          setSelectedNetworkRouteId(undefined);
          setSelectedVendorId(undefined);
        }
      }
    }
  }, [showEstimateForm, editingEstimateService, form, booking?.id]);

  const addRow = () => {
    setSelectedTemplates([...selectedTemplates, {
      templateId: '',
      templateName: '',
      quantity: 1,
      packingType: null,
      wePack: true, // Default to we are packing
    }]);
  };

  // Get unique packing type names that have at least one metric fitting the template
  const getAvailablePackingTypes = (templateId: string): string[] => {
    const template = inventoryTemplates?.find((t: any) => t.id === templateId);
    if (!template || !packingMetrics) return [];

    const length = template.length || 0;
    const width = template.width || 0;
    const height = template.height || 0;

    // Get all metrics that fit, then extract unique type names
    const fittingMetrics = packingMetrics.filter((metric) => {
      const maxL = Number(metric.maxLength) || Infinity;
      const maxW = Number(metric.maxWidth) || Infinity;
      const maxH = Number(metric.maxHeight) || Infinity;
      return length <= maxL && width <= maxW && height <= maxH;
    });

    // Get unique type names
    return [...new Set(fittingMetrics.map(m => m.packingType))];
  };

  // Get the best metric for a specific packing type and template
  const getBestMetricForType = (templateId: string, packingType: string) => {
    const template = inventoryTemplates?.find((t: any) => t.id === templateId);
    if (!template || !packingMetrics) return null;

    const length = template.length || 0;
    const width = template.width || 0;
    const height = template.height || 0;

    // Filter to metrics of this type that fit, sorted by closest fit
    const fittingMetrics = packingMetrics
      .filter((metric) => {
        if (metric.packingType !== packingType) return false;
        const maxL = Number(metric.maxLength) || Infinity;
        const maxW = Number(metric.maxWidth) || Infinity;
        const maxH = Number(metric.maxHeight) || Infinity;
        return length <= maxL && width <= maxW && height <= maxH;
      })
      .sort((a, b) => {
        // Sort by closest fit (smallest max dimensions that still fit)
        const aVolume = (Number(a.maxLength) || 999) * (Number(a.maxWidth) || 999) * (Number(a.maxHeight) || 999);
        const bVolume = (Number(b.maxLength) || 999) * (Number(b.maxWidth) || 999) * (Number(b.maxHeight) || 999);
        return aVolume - bVolume;
      });

    return fittingMetrics[0] || null;
  };

  const removeRow = (index: number) => {
    setSelectedTemplates(selectedTemplates.filter((_, i) => i !== index));
  };

  const updateRow = (index: number, field: keyof SelectedTemplate, value: any) => {
    setSelectedTemplates(prev => prev.map((t, i) => {
      if (i !== index) return t;
      if (field === 'templateId') {
        const template = inventoryTemplates?.find((tmpl: any) => tmpl.id === value);
        return { ...t, templateId: value, templateName: template?.name || '' };
      }
      return { ...t, [field]: value };
    }));
  };

  // Calculate total cubic feet, weight, material costs, and packing minutes from selected templates
  const metrics = useMemo(() => {
    let totalCubicFeet = 0;
    let totalWeight = 0;
    let totalMaterialCost = 0;
    let totalPackingMinutes = 0;

    selectedTemplates.forEach((item) => {
      const template = inventoryTemplates?.find((t: any) => t.id === item.templateId);
      if (template) {
        const cubicFeet = ((template.length || 0) * (template.width || 0) * (template.height || 0)) / 1728;
        totalCubicFeet += cubicFeet * item.quantity;
        totalWeight += (template.weight || 0) * item.quantity;

        // Calculate material cost and packing time only if AHC is packing AND type is selected
        if (item.wePack && item.templateId && item.packingType) {
          // Get best metric for the selected packing type
          const bestMetric = getBestMetricForType(item.templateId, item.packingType);
          if (bestMetric) {
            totalMaterialCost += calculateMaterialCost(
              template.length || 0,
              template.width || 0,
              template.height || 0,
              bestMetric.packingType as PackingTypes,
              item.quantity
            );

            // Get estimated minutes per item from best metric
            totalPackingMinutes += (Number(bestMetric.estimatedMinutesPerItem) || 0) * item.quantity;
          }
        }
      }
    });

    return {
      totalCubicFeet: Math.round(totalCubicFeet * 100) / 100,
      totalWeight: Math.round(totalWeight * 100) / 100,
      totalMaterialCost: Math.round(totalMaterialCost * 100) / 100,
      totalPackingMinutes,
    };
  }, [selectedTemplates, inventoryTemplates, packingMetrics]);

  // Generate scope text from selected templates
  const generatedScope = useMemo(() => {
    if (selectedTemplates.length === 0) return '';

    // Group by packing type
    const packingGroups: Record<string, { count: number; maxDims: string; templateNames: string[] }> = {};
    const shipperPacked: { count: number; templateNames: string[] } = { count: 0, templateNames: [] };

    selectedTemplates.forEach((item) => {
      const template = inventoryTemplates?.find((t: any) => t.id === item.templateId);
      if (!template) return;

      const dims = `${template.length}"×${template.width}"×${template.height}"`;

      if (item.wePack && item.packingType) {
        const typeLabel = item.packingType.replace(/_/g, ' ');
        if (!packingGroups[typeLabel]) {
          packingGroups[typeLabel] = { count: 0, maxDims: dims, templateNames: [] };
        }
        packingGroups[typeLabel].count += item.quantity;
        packingGroups[typeLabel].templateNames.push(`${item.quantity}x ${template.name}`);
        // Track largest dimensions
        const currentMax = packingGroups[typeLabel].maxDims;
        if (dims > currentMax) packingGroups[typeLabel].maxDims = dims;
      } else {
        shipperPacked.count += item.quantity;
        shipperPacked.templateNames.push(`${item.quantity}x ${template.name}`);
      }
    });

    const parts: string[] = [];

    // Add packing groups
    Object.entries(packingGroups).forEach(([type, data]) => {
      parts.push(`${type} ×${data.count} (max ${data.maxDims})`);
    });

    // Add shipper packed if any
    if (shipperPacked.count > 0) {
      parts.push(`Pre-packed ×${shipperPacked.count}`);
    }

    if (parts.length === 0) return '';
    return `Incl. ${parts.join(', ')}`;
  }, [selectedTemplates, inventoryTemplates]);

  // Auto-update scope when templates change (only if scope is empty or was auto-generated)
  const updateScopeWithGenerated = () => {
    const currentNotes = form.getFieldValue('notes') || '';
    // Check if current notes start with "Incl." (auto-generated) or is empty
    if (!currentNotes || currentNotes.startsWith('Incl.')) {
      form.setFieldValue('notes', generatedScope);
    } else {
      // Append to existing notes
      form.setFieldValue('notes', `${currentNotes}\n${generatedScope}`);
    }
  };

  const handleClose = () => {
    setShowEstimateForm(false);
    setEditingEstimateService(null);
    setCalculatedSupplies([]);
    setSupplyItems([]);
  };

  // Calculate all required supplies based on selected templates and packing types
  const calculateAllSupplies = async () => {
    setIsCalculatingSupplies(true);
    const suppliesMap = new Map<string, { supplyId: string; supplyName: string; quantity: number; unitCost?: number }>();

    try {
      for (const item of selectedTemplates) {
        // Only calculate for AHC PACK items with a selected packing type
        if (!item.wePack || !item.templateId || !item.packingType) continue;

        const template = inventoryTemplates?.find((t: any) => t.id === item.templateId);
        if (!template) continue;

        // Get the best metric for this packing type
        const bestMetric = getBestMetricForType(item.templateId, item.packingType);
        if (!bestMetric) continue;

        // Get material breakdown from API
        const response = await serviceMetricsApi.getMaterialBreakdown(
          bestMetric.id,
          template.length || 0,
          template.width || 0,
          template.height || 0
        );
        const breakdown = response?.data;

        if (breakdown?.materials) {
          for (const material of breakdown.materials) {
            // Calculate quantity needed for all items of this template
            // quantityNeeded is already per-unit from backend, multiply by template quantity
            const totalQuantity = material.quantityNeeded * item.quantity;

            if (suppliesMap.has(material.supplyId)) {
              // Add to existing supply
              const existing = suppliesMap.get(material.supplyId)!;
              existing.quantity += totalQuantity;
            } else {
              // New supply
              suppliesMap.set(material.supplyId, {
                supplyId: material.supplyId,
                supplyName: material.supplyName,
                quantity: totalQuantity,
                unitCost: material.billableRatePerUnit,
              });
            }
          }
        }
      }

      // Round up all quantities to nearest 0.25 with minimum of 1
      const roundToQuarter = (n: number) => Math.max(1, Math.ceil(n * 4) / 4);
      const supplies = Array.from(suppliesMap.values()).map(s => ({
        ...s,
        quantity: roundToQuarter(s.quantity),
      }));

      setCalculatedSupplies(supplies);

      // Also merge into supplyItems for saving
      setSupplyItems(prev => {
        // Start with existing manual supplies
        const merged = [...prev];
        for (const calc of supplies) {
          const existingIdx = merged.findIndex(item => item.supplyId === calc.supplyId);
          if (existingIdx >= 0) {
            // Update quantity if calculated is higher
            if (calc.quantity > merged[existingIdx].quantity) {
              merged[existingIdx] = { ...merged[existingIdx], quantity: calc.quantity, unitCost: calc.unitCost };
            }
          } else {
            // Add new calculated supply
            merged.push({
              supplyId: calc.supplyId,
              quantity: calc.quantity,
              unitCost: calc.unitCost,
            });
          }
        }
        return merged;
      });

      return supplies;
    } catch (err) {
      console.error("Failed to calculate supplies:", err);
      return [];
    } finally {
      setIsCalculatingSupplies(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const payload: any = {
        jobTicketId: booking!.id,
        clientId: booking?.clientId || undefined,
        serviceType: selectedServiceType,
        serviceDatetime: values.serviceDate ? values.serviceDate.toISOString() : undefined,
        startDatetime: values.startTime ? values.startTime.toISOString() : undefined,
        urgency: values.urgency || Urgency.NORMAL,
        vesselId: values.vesselId || undefined,
        status: JobStatus.ESTIMATED,
        scope: values.notes,
        duration: values.duration ? Number(values.duration) : undefined,
        travelTime: values.travelTime !== undefined && values.travelTime !== null ? Number(values.travelTime) : undefined,
        estimatedItemCount: selectedTemplates.reduce((sum, t) => sum + t.quantity, 0),
        estimateTemplates: selectedTemplates.length > 0 ? selectedTemplates.map(t => ({
          templateId: t.templateId,
          templateName: t.templateName,
          quantity: t.quantity,
          packingType: t.packingType,
          wePack: t.wePack,
        })) : undefined,
        addOnServices: selectedAddOnServices.length > 0 ? selectedAddOnServices : undefined,
        staffIds: selectedStaffIds.length > 0 ? selectedStaffIds : undefined,
        networkRouteId: selectedNetworkRouteId || null,
        vendorId: selectedVendorId || null,
        shuttleId: selectedShuttleId || null,
      };

      // Include supplies from supplyItems state (manually added + calculated)
      if (supplyItems.length > 0) {
        payload.supplyItems = supplyItems.map(s => ({
          supplyId: s.supplyId,
          quantity: s.quantity,
          unitCost: s.unitCost,
        }));
      }

      // Handle location and contact name
      if (useQuickLocation) {
        payload.tempLocationData = {
          name: values.contactName,
          city: values.quickCity,
          state: values.quickState || "",
          zip: values.quickZip,
        };
      } else if (values.locationId) {
        payload.shippingAddressId = values.locationId;
        // Still save contact name even with full address
        if (values.contactName) {
          payload.tempLocationData = {
            name: values.contactName,
          };
        }
      } else if (values.contactName) {
        // Save contact name even if no location specified
        payload.tempLocationData = {
          name: values.contactName,
        };
      }

      if (isEditing && editingEstimateService) {
        // Update existing estimate service
        await updateMutation.mutateAsync({
          id: editingEstimateService.id,
          ...payload,
        }, {
          onSuccess: () => {
            success({ message: "Estimate service updated successfully" });
            handleClose();
            qc.invalidateQueries({
              queryKey: BOOKING_KEYS.GET_BOOKING(booking?.id || ""),
            });
          },
          onError: (err: any) => {
            error({ message: err.message || "Failed to update estimate service" });
          },
        });
      } else {
        // Create new estimate service
        await createMutation.mutateAsync(payload, {
          onSuccess: () => {
            success({ message: "Estimate service added successfully" });
            handleClose();
            qc.invalidateQueries({
              queryKey: BOOKING_KEYS.GET_BOOKING(booking?.id || ""),
            });
          },
          onError: (err: any) => {
            error({ message: err.message || "Failed to add estimate service" });
          },
        });
      }
    } catch (err) {
      console.error("Validation failed:", err);
    }
  };

  if (!showEstimateForm) return null;

  const serviceTypes = [
    { label: "Collection", value: ServiceType.COLLECTION },
    { label: "Delivery", value: ServiceType.DELIVERY },
    { label: "Site Service", value: ServiceType.ON_SITE_SERVICE },
    { label: "Warehouse", value: ServiceType.WAREHOUSE_SERVICE },
    { label: "Fabrication", value: ServiceType.FABRICATION_SERVICE },
  ];

  return (
    <div className="bg-yellow-50 rounded-lg border-2 border-yellow-300 shadow-sm mb-6">
      {/* Header */}
      <div className="p-4 border-b border-yellow-200 flex justify-between items-center">
        <div>
          <h4 className="text-lg font-medium">
            {isEditing ? "Edit Estimate" : editingEstimateService ? "Duplicate Estimate" : "Add Estimate"}
          </h4>
          <p className="text-sm text-gray-500">
            {isEditing
              ? "Update estimate details - can be converted to full service later"
              : editingEstimateService
                ? "Create a copy of the estimate"
                : "Quick estimate - can be converted to full service later"
            }
          </p>
        </div>
        <button
          onClick={handleClose}
          className="p-1 hover:bg-yellow-100 rounded"
        >
          <X size={20} />
        </button>
      </div>

      {/* Service Type Tabs */}
      <div className="flex border-b border-yellow-200 bg-yellow-100/50">
        {serviceTypes.map((item) => {
          const Icon = serviceIcons[item.value]?.icon;
          const isSelected = selectedServiceType === item.value;
          return (
            <button
              key={item.value}
              type="button"
              className={`cursor-pointer px-6 transition-all ${
                isSelected
                  ? "py-4 text-lg font-bold text-yellow-900 border-b-3 border-yellow-600 bg-yellow-200"
                  : "py-3 text-sm text-gray-500 hover:text-gray-700 hover:bg-yellow-100"
              }`}
              onClick={() => {
                setSelectedServiceType(item.value);
                form.setFieldValue('serviceType', item.value);
              }}
            >
              <div className={`flex items-center space-x-2 ${isSelected ? "scale-105" : ""}`}>
                {Icon && <Icon size={isSelected ? 22 : 18} />}
                <span>{item.label}</span>
              </div>
            </button>
          );
        })}
      </div>

      <Form form={form} layout="vertical" className="p-6">
        <Form.Item name="serviceType" hidden>
          <Input />
        </Form.Item>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-4">
            {/* Contact Name */}
            <div className="p-4 bg-white rounded-lg border border-yellow-200">
              <Form.Item
                name="contactName"
                label={
                  <span className="flex items-center gap-2">
                    <User size={16} className="text-yellow-600" />
                    Contact / Client Name
                  </span>
                }
                className="mb-0"
              >
                <Input placeholder="Enter name for this estimate..." />
              </Form.Item>
            </div>

            {/* Location Section */}
            <div className="p-4 bg-white rounded-lg border border-yellow-200">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium flex items-center gap-2">
                  <MapPin size={16} className="text-yellow-600" />
                  Location
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Quick Location</span>
                  <Switch
                    checked={useQuickLocation}
                    onChange={setUseQuickLocation}
                    size="small"
                  />
                </div>
              </div>

              {useQuickLocation ? (
                <div className="grid grid-cols-3 gap-2">
                  <Form.Item name="quickCity" className="mb-0">
                    <Input placeholder="City" />
                  </Form.Item>
                  <Form.Item name="quickState" className="mb-0">
                    <Input placeholder="State" />
                  </Form.Item>
                  <Form.Item name="quickZip" className="mb-0">
                    <Input placeholder="Zip" />
                  </Form.Item>
                </div>
              ) : (
                <Form.Item name="locationId" className="mb-0">
                  <Select
                    placeholder="Select client address"
                    allowClear
                    options={client?.shippingAddresses?.map((addr: any) => ({
                      label: formatFullAddress(addr) || addr.addressName || "Address",
                      value: addr.id,
                    }))}
                  />
                </Form.Item>
              )}

              {/* Travel Time */}
              <div className="mt-3 pt-3 border-t border-yellow-100">
                <Form.Item
                  name="travelTime"
                  label={
                    <span className="flex items-center gap-1">
                      <Car size={14} className="text-yellow-600" />
                      Travel Time
                    </span>
                  }
                  className="mb-0"
                  initialValue={0.5}
                >
                  <InputNumber
                    min={0}
                    step={0.25}
                    className="w-full"
                    placeholder="Travel time"
                    addonAfter="hrs"
                  />
                </Form.Item>
              </div>
            </div>

            {/* Inventory Templates */}
            <div className="p-4 bg-white rounded-lg border border-yellow-200">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Package size={16} className="text-yellow-600" />
                  Item Templates
                </label>
                <button
                  type="button"
                  onClick={addRow}
                  className="px-2 py-1 text-sm bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 flex items-center gap-1"
                >
                  <Plus size={14} />
                  Add Item
                </button>
              </div>

              {/* Metrics Summary */}
              {selectedTemplates.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mb-3 text-xs">
                  <span className="font-medium bg-yellow-100 px-2 py-1 rounded">
                    {metrics.totalCubicFeet} ft³
                  </span>
                  <span className="font-medium bg-yellow-100 px-2 py-1 rounded">
                    {metrics.totalWeight} lbs
                  </span>
                  {metrics.totalMaterialCost > 0 && (
                    <span className="font-medium bg-green-100 text-green-700 px-2 py-1 rounded flex items-center gap-1">
                      <Package size={12} />
                      ${metrics.totalMaterialCost.toFixed(2)} materials
                    </span>
                  )}
                  {metrics.totalPackingMinutes > 0 && (
                    <span className="font-medium bg-blue-100 text-blue-700 px-2 py-1 rounded flex items-center gap-1">
                      <Clock size={12} />
                      {(metrics.totalPackingMinutes / 60).toFixed(1)} AH hrs
                    </span>
                  )}
                </div>
              )}

              {selectedTemplates.length > 0 ? (
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {selectedTemplates.map((item, index) => {
                    const template = inventoryTemplates?.find((t: any) => t.id === item.templateId);

                    // Get available packing types for this template
                    const availableTypes = item.templateId ? getAvailablePackingTypes(item.templateId) : [];

                    // Get the best metric for the selected packing type
                    const bestMetric = item.wePack && item.templateId && item.packingType
                      ? getBestMetricForType(item.templateId, item.packingType)
                      : null;

                    // Calculate material cost when AHC packs using best metric for selected type
                    const itemMaterialCost = item.wePack && template && bestMetric
                      ? calculateMaterialCost(
                          template.length || 0,
                          template.width || 0,
                          template.height || 0,
                          bestMetric.packingType as PackingTypes,
                          item.quantity
                        )
                      : 0;

                    return (
                      <div key={index} className={`p-2 rounded border ${item.wePack ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                        <div className="flex items-center gap-2">
                          {/* Pack Toggle */}
                          <Tooltip title={item.wePack ? "AHC will pack this item" : "Pre-packed by shipper"}>
                            <button
                              type="button"
                              onClick={() => updateRow(index, 'wePack', !item.wePack)}
                              className={`px-2 py-1 text-xs rounded font-medium transition-colors whitespace-nowrap ${
                                item.wePack
                                  ? 'bg-green-500 text-white'
                                  : 'bg-gray-300 text-gray-600'
                              }`}
                            >
                              {item.wePack ? 'AHC PACK' : 'SHIPPER'}
                            </button>
                          </Tooltip>

                          {/* Template Select */}
                          <Select
                            placeholder="Select template"
                            value={item.templateId || undefined}
                            onChange={(val) => updateRow(index, 'templateId', val)}
                            className="flex-1"
                            size="small"
                            showSearch
                            filterOption={(input, option) =>
                              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            options={inventoryTemplates?.map((t: any) => ({
                              label: `${t.name} (${t.length}"×${t.width}"×${t.height}")`,
                              value: t.id,
                            }))}
                          />

                          {/* Quantity */}
                          <InputNumber
                            min={1}
                            value={item.quantity}
                            onChange={(val) => updateRow(index, 'quantity', val || 1)}
                            className="w-16"
                            size="small"
                            placeholder="Qty"
                          />

                          {/* Packing Type - Dropdown of fitting types for AHC pack, all types for shipper */}
                          {item.wePack ? (
                            <Select
                              placeholder="Select type"
                              value={item.packingType}
                              onChange={(val) => updateRow(index, 'packingType', val)}
                              className="w-36"
                              size="small"
                              disabled={!item.templateId}
                              options={availableTypes.map((type) => ({
                                label: type.replace(/_/g, ' '),
                                value: type,
                              }))}
                              notFoundContent={<span className="text-xs text-gray-400">No types fit</span>}
                            />
                          ) : (
                            <Select
                              placeholder="Packing type"
                              value={item.packingType}
                              onChange={(val) => updateRow(index, 'packingType', val)}
                              className="w-36"
                              size="small"
                              allowClear
                              options={Object.values(PackingTypes).map((type) => ({
                                label: type.replace(/_/g, ' '),
                                value: type,
                              }))}
                            />
                          )}

                          {/* Material Cost Display (only if AHC packs) */}
                          {item.wePack && itemMaterialCost > 0 ? (
                            <Tooltip title="Estimated material cost">
                              <span className="text-xs font-medium text-green-700 w-16 text-right">
                                ${itemMaterialCost.toFixed(0)}
                              </span>
                            </Tooltip>
                          ) : (
                            <span className="w-16" />
                          )}

                          {/* Remove Button */}
                          <button
                            type="button"
                            onClick={() => removeRow(index)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-sm text-gray-400 text-center py-4 border-2 border-dashed border-gray-200 rounded">
                  Click "Add Item" to add item templates
                </div>
              )}

              {/* Material Cost Info */}
              {selectedTemplates.some(t => t.wePack && t.packingType) && (
                <div className="mt-3 pt-3 border-t border-yellow-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <Package size={14} />
                      <span>Material costs based on surface area × rate/sq ft</span>
                    </div>
                    <Button
                      size="small"
                      type="primary"
                      ghost
                      loading={isCalculatingSupplies}
                      onClick={async () => {
                        await calculateAllSupplies();
                        updateScopeWithGenerated();
                      }}
                      className="text-xs"
                    >
                      Calculate Supplies + Scope
                    </Button>
                  </div>
                </div>
              )}

              {/* Calculated Supplies */}
              {calculatedSupplies.length > 0 && (
                <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-purple-800 flex items-center gap-2">
                      <Package size={14} />
                      Required Supplies ({calculatedSupplies.length})
                    </h4>
                    <span className="text-xs text-purple-600">
                      ${calculatedSupplies.reduce((sum, s) => sum + (s.quantity * (s.unitCost || 0)), 0).toFixed(2)} total
                    </span>
                  </div>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {calculatedSupplies.map((supply, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-white p-2 rounded border border-purple-100 text-xs">
                        <span className="font-medium">{supply.supplyName}</span>
                        <span className="text-purple-700">
                          {supply.quantity} × ${(supply.unitCost || 0).toFixed(2)} = ${(supply.quantity * (supply.unitCost || 0)).toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Supplies */}
            <div className="p-4 bg-white rounded-lg border border-yellow-200">
              <Supplies
                value={supplyItems}
                onChange={setSupplyItems}
                viewOnly={false}
                skipStockCheck={true}
              />
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-4">
            {/* Network Connect */}
            <NetworkConnect
              serviceType={selectedServiceType}
              networkRouteId={selectedNetworkRouteId}
              vendorId={selectedVendorId}
              shuttleId={selectedShuttleId}
              onNetworkRouteChange={(val) => {
                setSelectedNetworkRouteId(val);
                if (val) { setSelectedVendorId(undefined); setSelectedShuttleId(undefined); }
              }}
              onVendorChange={(val) => {
                setSelectedVendorId(val);
                if (val) { setSelectedNetworkRouteId(undefined); setSelectedShuttleId(undefined); }
              }}
              onShuttleChange={(val) => {
                setSelectedShuttleId(val);
                if (val) { setSelectedNetworkRouteId(undefined); setSelectedVendorId(undefined); }
              }}
            />

            {/* Date & Time */}
            <div className="p-4 bg-white rounded-lg border border-yellow-200">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium">Date & Time</label>
                <span className="text-sm font-bold text-yellow-700 bg-yellow-100 px-2 py-0.5 rounded flex items-center gap-1">
                  {(() => {
                    const Icon = serviceIcons[selectedServiceType]?.icon;
                    return Icon ? <Icon size={14} /> : null;
                  })()}
                  {serviceTypes.find(s => s.value === selectedServiceType)?.label}
                </span>
              </div>
              <Form.Item name="serviceDate" label="Service Date" className="mb-3">
                <ServiceDatePicker className="w-full" serviceType={selectedServiceType} />
              </Form.Item>
              <div className="grid grid-cols-3 gap-3">
                <Form.Item name="startTime" label="Start Time" rules={[{ required: true, message: "Please select start time" }]} className="mb-0">
                  <TimePicker
                    className="w-full"
                    format="h:mm A"
                    use12Hours
                    minuteStep={15}
                    placeholder="Select time"
                  />
                </Form.Item>
                <Form.Item label="Quick" className="mb-0">
                  <Select
                    options={[
                      { label: "9AM", value: 9 },
                      { label: "10AM", value: 10 },
                      { label: "11AM", value: 11 },
                      { label: "12PM", value: 12 },
                      { label: "1PM", value: 13 },
                      { label: "2PM", value: 14 },
                      { label: "3PM", value: 15 },
                      { label: "4PM", value: 16 },
                    ]}
                    onChange={(hour: number) => {
                      const serviceDate = form.getFieldValue("serviceDate");
                      const baseDate = serviceDate ? dayjs(serviceDate) : dayjs();
                      const combinedDateTime = baseDate.hour(hour).minute(0).second(0);
                      form.setFieldsValue({ startTime: combinedDateTime });
                    }}
                    placeholder="Time"
                  />
                </Form.Item>
                <Form.Item
                  name="duration"
                  label={
                    <span className="flex items-center gap-1">
                      <Clock size={14} className="text-yellow-600" />
                      Duration
                    </span>
                  }
                  rules={[{ required: true, message: "Please enter duration" }]}
                  className="mb-0"
                >
                  <InputNumber
                    min={0.25}
                    step={0.25}
                    className="w-full"
                    placeholder="Est. duration"
                    addonAfter={
                      <Tooltip title={`Calculate: ${metrics.totalPackingMinutes} min packing + 20 min base`}>
                        <button
                          type="button"
                          onClick={() => {
                            // Packing minutes + 20 min base, convert to hours
                            const totalMinutes = metrics.totalPackingMinutes + 20;
                            const hours = Math.round((totalMinutes / 60) * 4) / 4; // Round to nearest 0.25
                            form.setFieldValue('duration', hours);
                          }}
                          className="flex items-center gap-1 text-yellow-600 hover:text-yellow-700"
                        >
                          <Calculator size={14} />
                        </button>
                      </Tooltip>
                    }
                  />
                </Form.Item>
              </div>
            </div>

            {/* Urgency & Vessel */}
            <div className="p-4 bg-white rounded-lg border border-yellow-200">
              <div className="grid grid-cols-2 gap-3">
                <Form.Item
                  name="urgency"
                  label="Urgency"
                  initialValue={Urgency.NORMAL}
                  className="mb-0"
                >
                  <Select
                    placeholder="Select urgency"
                    options={[
                      { label: "Low", value: Urgency.LOW },
                      { label: "Normal", value: Urgency.NORMAL },
                      { label: "High", value: Urgency.HIGH },
                      { label: "Urgent", value: Urgency.URGENT },
                    ]}
                  />
                </Form.Item>
                <Form.Item name="vesselId" label="Vessel" className="mb-0">
                  <Select
                    placeholder="Select vessel"
                    allowClear
                    onChange={(val) => {
                      if (val === "") {
                        form.setFieldValue("vesselId", null);
                      }
                    }}
                    options={[
                      { value: "", label: "No Vehicle" },
                      ...(vessels?.map((v: any) => ({
                        label: v.name,
                        value: v.id,
                        vesselStatus: v.vesselStatus,
                        maintenance: v.maintenance,
                        isActive: v.isActive,
                      })) || []),
                    ]}
                    optionRender={(option) => {
                      const vesselStatus = option.data.vesselStatus as VesselStatus;
                      const maintenance = option.data.maintenance as Maintenance;
                      const isActive = option.data.isActive as boolean;
                      const isUnderMaintenance = vesselStatus === VesselStatus.MAINTENANCE ||
                        maintenance === Maintenance.IN_SERVICE ||
                        maintenance === Maintenance.SCHEDULED;
                      const isInactive = isActive === false;
                      const isInUse = vesselStatus === VesselStatus.IN_USE;

                      return (
                        <div className="flex items-center justify-between">
                          <span className={isInactive ? 'text-gray-400' : ''}>
                            {option.label}
                          </span>
                          {isInactive && (
                            <span className="text-xs text-red-500">Inactive</span>
                          )}
                          {isUnderMaintenance && !isInactive && (
                            <span className="flex items-center gap-1 text-xs text-orange-500">
                              <Wrench size={12} />
                              Maintenance
                            </span>
                          )}
                          {isInUse && !isInactive && !isUnderMaintenance && (
                            <span className="text-xs text-blue-500">In Use</span>
                          )}
                        </div>
                      );
                    }}
                  />
                </Form.Item>
              </div>
            </div>

            {/* Staff Assignment */}
            <div className="p-4 bg-white rounded-lg border border-blue-200">
              <label className="text-sm font-medium flex items-center gap-2 mb-3">
                <User size={16} className="text-blue-600" />
                Staff (Optional)
              </label>
              <StaffAssignment
                value={selectedStaffIds}
                onChange={setSelectedStaffIds}
                serviceDate={serviceDate ? dayjs(serviceDate) : null}
              />
            </div>

            {/* Notes / Scope + Add-On Services */}
            <div className="grid grid-cols-3 gap-3">
              {/* Notes / Scope - 2/3 width */}
              <div className="col-span-2 p-4 bg-white rounded-lg border border-yellow-200">
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium">Notes / Scope</label>
                  {generatedScope && (
                    <Tooltip title="Generate scope from selected templates">
                      <Button
                        size="small"
                        type="link"
                        onClick={updateScopeWithGenerated}
                        className="text-xs p-0 h-auto"
                      >
                        Generate Scope
                      </Button>
                    </Tooltip>
                  )}
                </div>
                <Form.Item name="notes" rules={[{ required: true, message: "Please enter scope" }]} className="mb-0">
                  <TextArea rows={3} placeholder="Any additional notes or scope details..." />
                </Form.Item>
                {generatedScope && (
                  <div className="mt-2 text-xs text-gray-500 bg-gray-50 p-2 rounded">
                    <span className="font-medium">Preview: </span>{generatedScope}
                  </div>
                )}
              </div>

              {/* Add-On Services - 1/3 width */}
              <div className="col-span-1 p-3 bg-white rounded-lg border border-yellow-200">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium flex items-center gap-1">
                    <Wrench size={12} className="text-yellow-600" />
                    Add-Ons
                  </label>
                  {selectedAddOnServices.length > 0 && (
                    <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded">
                      {selectedAddOnServices.length}
                    </span>
                  )}
                </div>

                {addOnServices.length > 0 ? (
                  <div className="space-y-1 max-h-28 overflow-y-auto">
                    {addOnServices.map((service) => (
                      <div
                        key={service.id}
                        className={`flex items-center gap-1.5 p-1.5 rounded cursor-pointer transition-colors text-xs ${
                          selectedAddOnServices.includes(service.id)
                            ? 'bg-yellow-100'
                            : 'hover:bg-gray-50'
                        }`}
                        onClick={() => {
                          if (selectedAddOnServices.includes(service.id)) {
                            setSelectedAddOnServices(selectedAddOnServices.filter(id => id !== service.id));
                          } else {
                            setSelectedAddOnServices([...selectedAddOnServices, service.id]);
                          }
                        }}
                      >
                        <Checkbox
                          checked={selectedAddOnServices.includes(service.id)}
                          className="scale-90"
                          onChange={(e) => {
                            e.stopPropagation();
                            if (e.target.checked) {
                              setSelectedAddOnServices([...selectedAddOnServices, service.id]);
                            } else {
                              setSelectedAddOnServices(selectedAddOnServices.filter(id => id !== service.id));
                            }
                          }}
                        />
                        <span className="truncate">{service.name}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-gray-400 text-center py-2">
                    No add-ons
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end items-center gap-3 mt-6 pt-4 border-t border-yellow-200">
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={createMutation.isPending || updateMutation.isPending}
            className="bg-yellow-500 hover:bg-yellow-600 border-yellow-500"
          >
            {isEditing ? "Update Estimate" : editingEstimateService ? "Duplicate Estimate" : "Add Estimate"}
          </Button>
          <span className="text-sm font-bold text-yellow-700 bg-yellow-100 px-3 py-1.5 rounded flex items-center gap-1.5">
            {(() => {
              const Icon = serviceIcons[selectedServiceType]?.icon;
              return Icon ? <Icon size={16} /> : null;
            })()}
            {serviceTypes.find(s => s.value === selectedServiceType)?.label}
          </span>
        </div>
      </Form>
    </div>
  );
};
